import { useEffect, useState } from 'react';
import { Badge, Card, Col, Row, Spinner } from 'react-bootstrap';
import Section from 'components/base/Section';
import { presenceService, Presence } from 'services/api';
import { useAuth } from 'providers/AuthProvider';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';

const AllPresences = () => {
  const [presences, setPresences] = useState<Presence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchPresences = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await presenceService.listPresences();
        setPresences(data);
      } catch (err) {
        setError('Erro ao carregar as presenças');
      } finally {
        setLoading(false);
      }
    };
    fetchPresences();
  }, [isAuthenticated]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const MessageCell = ({ message }: { message: string }) => {
    if (!message) {
      return <small className="text-muted">Sem mensagem</small>;
    }
    return (
      <div style={{ maxWidth: '500px' }}>
        <small className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{message}</small>
      </div>
    );
  };

  const columns: ColumnDef<Presence>[] = [
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => <strong>{row.original.name}</strong>
    },
    {
      accessorKey: 'email',
      header: 'E-mail',
      cell: ({ row }) => <small>{row.original.email}</small>
    },
    {
      accessorKey: 'createdAt',
      header: 'Data de Confirmação',
      cell: ({ row }) => <small>{formatDate(row.original.createdAt)}</small>
    },
    {
      accessorKey: 'message',
      header: 'Mensagem',
      size: 400,
      cell: ({ row }) => <MessageCell message={row.original.message} />
    }
  ];

  const table = useAdvanceTable({
    data: presences,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  if (!isAuthenticated) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <p>Você precisa estar logado para ver as presenças.</p>
        </div>
      </Section>
    );
  }

  if (loading) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section small className="py-5">
        <div className="text-center">
          <p className="text-danger">{error}</p>
        </div>
      </Section>
    );
  }

  return (
    <div className="pt-5 mb-9">
      <Section small className="py-0">
        <Row className="align-items-center justify-content-between g-3 mb-4">
          <Col xs="auto">
            <h2 className="mb-0">Todas as Presenças</h2>
          </Col>
          <Col xs="auto">
            <p className="mb-0 text-muted">Total de {presences.length} presenças confirmadas</p>
          </Col>
        </Row>
        <Card>
          <Card.Body className="p-0">
            {presences.length === 0 ? (
              <div className="text-center p-5">
                <p className="mb-0">Nenhuma presença encontrada.</p>
              </div>
            ) : (
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  tableProps={{
                    className: 'mb-0',
                    hover: true
                  }}
                  headerClassName="bg-light"
                />
                <AdvanceTableFooter />
              </AdvanceTableProvider>
            )}
          </Card.Body>
        </Card>
      </Section>
    </div>
  );
};

export default AllPresences; 